import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import LoginPageLayout from "../../components/LoginPageLayout";
import AuthForm from "../../components/AuthForm";
import stylesForms from "../../components/styles/auth-forms.module.css";
import { triggerGa } from "../../utils/gaUtils";
import { PROD_BD_API_URL } from "../../api/axiosInstance";
import { Box, Button } from "@mui/material";

export default function LoginPage({ openSignupForm }) {
  const { loginMutation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage || null,
  );

  useEffect(() => {
    if (location.state?.successMessage) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate]);

  const handleGoogleLogin = () => {
    const redirect_url = window.location.origin + window.location.pathname;
    window.location.href = `${PROD_BD_API_URL}oauth-login?redirect=${encodeURIComponent(redirect_url)}`;
  };

  const validateForm = () => {
    if (!email) {
      setError("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!password) {
      setError("Password is required");
      return false;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    triggerGa({
      category: "Login",
      action: "button_click",
      label: "login",
      label2: "",
      page: "login",
      screenName: "login",
    });
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    if (!validateForm()) {
      return;
    }
    loginMutation.mutate({
      email,
      password,
      remember_me: rememberMe,
      setError,
    });
  };

  return (
    <LoginPageLayout openSignupForm={openSignupForm}>
      <Box className={stylesForms.formGroup} sx={{ mb: "1rem" }}>
        <Button
          type="button"
          onClick={handleGoogleLogin}
          className={stylesForms.input}
          sx={{
            backgroundColor: "#E38125",
            color: "white",
            p: "0.75rem",
            borderRadius: "0.5rem",
            fontWeight: "bold",
            cursor: "pointer",
            border: "none",
            width: "100%",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#E38125",
            },
          }}
        >
          Continue with Google
        </Button>
      </Box>
      <AuthForm
        title="Log into your Wishlink Brand Account"
        onSubmit={onSubmit}
        buttonText="Log In"
        loading={loginMutation.isPending}
        error={error}
        successMessage={successMessage}
        additionalLinks={
          <Link
            to="/forgot-password"
            className={stylesForms.blueLink}
            onClick={() =>
              triggerGa({
                category: "Login",
                action: "button_click",
                label: "forgot_password",
                label2: "",
                page: "login",
                screenName: "login",
              })
            }
          >
            Forgot Password?
          </Link>
        }
      >
        <div className={stylesForms.formGroup}>
          <label htmlFor="email" className={stylesForms.label}>
            Email
          </label>
          <input
            type="email"
            id="email"
            autoComplete="username"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            className={stylesForms.input}
            required
            aria-label="Email address"
            aria-required="true"
            aria-invalid={!!error && !email}
          />
        </div>
        <div className={stylesForms.formGroup}>
          <label htmlFor="password" className={stylesForms.label}>
            Password
          </label>
          <input
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            className={stylesForms.input}
            required
            aria-label="Password"
            aria-required="true"
            aria-invalid={!!error && !password}
            minLength={6}
          />
        </div>
        <div className={stylesForms.checkbox}>
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => {
              setRememberMe(e.target.checked);
              triggerGa({
                category: "Login",
                action: "button_click",
                label: "remember_me",
                label2: "",
                page: "login",
                screenName: "login",
                eventValue: e.target.checked ? 1 : 0,
              });
            }}
            className={stylesForms.checkboxInput}
            aria-label="Remember me"
          />
          <label htmlFor="rememberMe" className={stylesForms.checkboxLabel}>
            Remember me
          </label>
        </div>
      </AuthForm>
    </LoginPageLayout>
  );
}
